import React, { type FC, useMemo } from "react";
import { BusinessLotValue, BusinessMeasureValue, BusinessStockObjValue } from "@src/components/business-data-display";
import { BusinessForm } from "@src/components/business-form";
import { DetailList } from "@shared/ui/detail-row";
import { EmptyState } from "@shared/ui/empty-state";
import FormFooter from "@shared/ui/form-footer";
import { FormPanel } from "@shared/ui/form-panel";
import { Input } from "@shared/ui/input";
import { measureEndDecorator } from "@src/helpers/business/measureEndDecorator";
import type { StockActInvContent } from "../../../../../../api/stockActInvs";
import type { StorePlaceRemainder } from "../../../../../../api/storePlaces";
import {
    formatInventoryCells,
    isStockFullInventoryContentInventoried,
    isStockFullInventoryContentLotBased,
} from "../../helpers/formatStockFullInventoryContent";
import type { UseStockFullInventoryCardMutationsResult } from "../../useStockFullInventoryCardMutations";
import {
    stockFullInventoryContentFormConfig,
    type StockFullInventoryContentFormValues,
} from "./stockFullInventoryContent.form-config";
import "./StockFullInventoryContentForm.scss";

/** Пропсы компонента {@link StockFullInventoryContentForm}. */
export interface StockFullInventoryContentFormProps {
    /** Код строки состава АИ. */
    contentCode: number;
    /** Строки текущего АИ. */
    contents: StockActInvContent[];
    /** Мутация обновления фактических количеств. */
    updateFactQtiesMutation: UseStockFullInventoryCardMutationsResult["updateFactQtiesMutation"];
}

/** Форма инвентаризации строки АИ. */
export const StockFullInventoryContentForm: FC<StockFullInventoryContentFormProps> = ({
    contentCode,
    contents,
    updateFactQtiesMutation,
}) => {
    const content = contents.find((item) => item.Code === contentCode);

    if (content == null) {
        return <EmptyState text="Строка АИ не найдена" />;
    }

    if (!isStockFullInventoryContentLotBased(content)) {
        return <EmptyState text="Инвентаризация без партии пока не поддерживается" />;
    }

    return <StockFullInventoryContentFormBody content={content} updateFactQtiesMutation={updateFactQtiesMutation} />;
};

interface StockFullInventoryContentFormBodyProps {
    content: StockActInvContent;
    updateFactQtiesMutation: UseStockFullInventoryCardMutationsResult["updateFactQtiesMutation"];
}

const StockFullInventoryContentFormBody: FC<StockFullInventoryContentFormBodyProps> = ({
    content,
    updateFactQtiesMutation,
}) => {
    const cellRemainders = useMemo(() => resolveCellRemainders(content), [content]);
    const isInventoried = isStockFullInventoryContentInventoried(content);
    const unitDecorator = measureEndDecorator(content.Measure);
    const initialValues = useMemo(
        (): StockFullInventoryContentFormValues => ({
            factQty:
                cellRemainders.length === 0 ? content.FactReminder : cellRemainders.reduce((sum, item) => sum + item.Qty, 0),
            factQtyByStorePlaces: cellRemainders.map((item) => ({
                spCode: item.SpCode,
                cellText: item.StorePlace?.Num ?? `Ячейка ${item.SpCode}`,
                factQty: item.Qty,
            })),
        }),
        [cellRemainders, content.FactReminder],
    );

    const handleSubmit = async (values: StockFullInventoryContentFormValues): Promise<void> => {
        const domainValues = stockFullInventoryContentFormConfig.toDomain(values);
        const factQty = domainValues.factQty;

        if (factQty == null) {
            return;
        }

        await updateFactQtiesMutation.mutateAsync({
            contentCode: content.Code,
            factQty,
            factQtyByStorePlaces: domainValues.factQtyByStorePlaces,
        });
    };

    return (
        <FormPanel<StockFullInventoryContentFormValues>
            useDefaultFooter={false}
            formProps={{
                formConfig: stockFullInventoryContentFormConfig,
                initialValues,
                ready: true,
                onSubmit: handleSubmit,
            }}
            footer={({ submitForm, isFormSubmitting }) => (
                <FormFooter confirmText="Сохранить" confirmLoading={isFormSubmitting} onConfirm={submitForm} />
            )}
        >
            {(form) => (
                <div className="stock_full_inventory_content_form">
                    <DetailList layout="tiles" columns="auto" minWidth={112}>
                        <DetailList.Row label="Номенклатура" value={<BusinessStockObjValue stockObj={content.StockObj} />} />
                        <DetailList.Row
                            label="Факт. кол-во"
                            value={<BusinessMeasureValue value={content.FactReminder} measure={content.Measure} />}
                        />
                        <DetailList.Row label="Партия" value={<BusinessLotValue lot={content.Lot} />} />
                        <DetailList.Row label="Ячейки хранения" value={formatInventoryCells(content.PlacedCellRemainders)} />
                        <DetailList.Row label="Партия инвентаризирована" value={isInventoried ? "Да" : "Нет"} />
                    </DetailList>
                    {cellRemainders.length === 0 ? (
                        <BusinessForm.Field name="factQty">
                            <Input
                                type="number"
                                {...form.field("factQty").getNumberInputProps()}
                                endDecorator={unitDecorator}
                            />
                        </BusinessForm.Field>
                    ) : (
                        initialValues.factQtyByStorePlaces.map((item, index) => (
                            <BusinessForm.Field key={item.spCode} name={`factQtyByStorePlaces.${index}.factQty`}>
                                <Input
                                    type="number"
                                    {...form.field(`factQtyByStorePlaces.${index}.factQty`).getNumberInputProps()}
                                    endDecorator={unitDecorator}
                                />
                            </BusinessForm.Field>
                        ))
                    )}
                </div>
            )}
        </FormPanel>
    );
};

function resolveCellRemainders(content: StockActInvContent): StorePlaceRemainder[] {
    if (content.PlacedCellRemainders != null && content.PlacedCellRemainders.length > 0) {
        return content.PlacedCellRemainders;
    }

    return [];
}
